import { nextTick, ref } from 'vue';

export function useNewBuildingObservers() {
  const isNewBuildingProjectCardInViewport = ref(true);
  const isNewBuildingProjectCardOnTop = ref(false);

  let newBuildingProjectCardIntersectionObserver: IntersectionObserver | undefined;
  let newBuildingDesignSwitchObserver: IntersectionObserver | undefined;

  const setUpNewBuildingProjectCardObserver = (containerRef: Element | undefined) => {
    // SSR safety: Skip if we're in SSR or during hydration
    if (import.meta.env.SSR || typeof window === 'undefined') {
      return;
    }

    const newBuildingProjectCardContainer = containerRef;
    if (
      typeof IntersectionObserver === 'undefined' ||
      !newBuildingProjectCardContainer ||
      !newBuildingProjectCardContainer.parentNode ||
      newBuildingProjectCardIntersectionObserver
    ) {
      return;
    }

    newBuildingProjectCardIntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        // Safari-specific fix: Check if entries exist
        if (!entries || entries.length === 0 || !entries[0]) {
          return;
        }
        isNewBuildingProjectCardInViewport.value =
          entries[0].isIntersecting ||
          (entries[0].rootBounds?.height || 0) < entries[0].boundingClientRect.top;
      },
      {
        // Add rootMargin to help Safari detect intersection more reliably
        rootMargin: '10px 0px 10px 0px',
        threshold: [0, 0.1, 1],
      },
    );
    newBuildingProjectCardIntersectionObserver.observe(newBuildingProjectCardContainer);
  };

  const setUpNewBuildingStickyBarObserver = (stickyBarRef: Element | undefined) => {
    // SSR safety: Skip if we're in SSR or during hydration
    if (import.meta.env.SSR || typeof window === 'undefined') {
      return;
    }

    const newBuildingStickyBar = stickyBarRef;
    if (
      typeof IntersectionObserver === 'undefined' ||
      !newBuildingStickyBar ||
      !newBuildingStickyBar.parentNode ||
      newBuildingDesignSwitchObserver
    ) {
      return;
    }

    newBuildingDesignSwitchObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        // Safari-specific fix: Check if entries exist
        if (!entries || entries.length === 0 || !entries[0]) {
          return;
        }
        isNewBuildingProjectCardOnTop.value = !entries[0].isIntersecting && !!entries[0].rootBounds;
      },
      {
        rootMargin: '-20px 0px 0px 0px',
        threshold: [0, 0.1, 1],
      },
    );
    newBuildingDesignSwitchObserver.observe(newBuildingStickyBar);
  };

  const cleanUpNewBuildingProjectCardObserver = () => {
    // SSR safety: Only cleanup if not in SSR environment
    if (
      !import.meta.env.SSR &&
      typeof window !== 'undefined' &&
      newBuildingProjectCardIntersectionObserver
    ) {
      newBuildingProjectCardIntersectionObserver.disconnect();
      newBuildingProjectCardIntersectionObserver = undefined;
    }
  };

  const cleanUpNewBuildingStickyBarObserver = () => {
    // SSR safety: Only cleanup if not in SSR environment
    if (!import.meta.env.SSR && typeof window !== 'undefined' && newBuildingDesignSwitchObserver) {
      newBuildingDesignSwitchObserver.disconnect();
      newBuildingDesignSwitchObserver = undefined;
    }
  };

  const setupObservers = (containerRef: Element | undefined, stickyBarRef: Element | undefined) => {
    // Cleanup existing observers before setting up new ones (Safari compatibility)
    cleanupObservers();

    nextTick(() => {
      // Additional Safari timing fix - small delay to ensure DOM is ready
      setTimeout(() => {
        setUpNewBuildingProjectCardObserver(containerRef);
        setUpNewBuildingStickyBarObserver(stickyBarRef);
      }, 50);
    });
  };

  const cleanupObservers = () => {
    cleanUpNewBuildingProjectCardObserver();
    cleanUpNewBuildingStickyBarObserver();
  };

  // Note: Lifecycle management is handled by the parent component
  // No composable lifecycle hooks needed since the component calls setupObservers/cleanupObservers directly

  return {
    isNewBuildingProjectCardInViewport,
    isNewBuildingProjectCardOnTop,
    setupObservers,
    cleanupObservers,
  };
}
